import {
  walletAPIClientAtom,
  walletCapabilitiesAtom,
  walletInfoAtom,
} from "@/store/wallet-api.store";
import {
  connectionStatusAtom,
  coreAtom,
  loadingAtom,
  oneClickAuthPayloadAtom,
  showBackToBrowserModalAtom,
  addVerifyContextAtom,
  getVerifyContextKey,
  walletKitAtom,
} from "@/store/walletKit.store";
import { OneClickAuthPayload } from "@/types/types";
import {
  getErrorMessage,
  getSafeRedirectUrl,
  isBIP122Chain,
  isCosmosChain,
  isCosmosSupportEnabled,
  isEIP155Chain,
  isRippleChain,
  isSolanaChain,
  isSolanaSupportEnabled,
  isTezosChain,
  isTezosSupportEnabled,
  isXRPLSupportEnabled,
} from "@/utils/helper.util";
import { reportZodError } from "@/utils/sentryZod";
import { WalletKitTypes } from "@reown/walletkit";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { SignClientTypes } from "@walletconnect/types";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ZodError } from "zod";
import { isWalletRequest } from "../utils/helper.util";
import { handleBIP122Request } from "./requestHandlers/BIP122";
import { handleCosmosRequest } from "./requestHandlers/Cosmos";
import { handleEIP155Request } from "./requestHandlers/EIP155";
import { handleXrpRequest } from "./requestHandlers/Ripple";
import { handleSolanaRequest } from "./requestHandlers/Solana";
import { handleTezosRequest } from "./requestHandlers/Tezos";
import { Errors, rejectRequest } from "./requestHandlers/utils";
import { handleWalletRequest } from "./requestHandlers/Wallet";
import useAccounts from "./useAccounts";
import {
  queryKey as pendingProposalsQueryKey,
  useQueryFn as usePendingProposalsQueryFn,
} from "./usePendingProposals";
import { queryKey as sessionsQueryKey } from "./useSessions";

function useWalletConnectStatus() {
  const core = useAtomValue(coreAtom);
  const [connectionStatus, setConnectionStatus] = useAtom(connectionStatusAtom);
  const { t } = useTranslation();

  useEffect(() => {
    if (core.relayer.connected && connectionStatus !== "connected") {
      setConnectionStatus("connected");
      enqueueSnackbar(t("connect.connected"), {
        connected: true,
        variant: "connectionNotification",
        anchorOrigin: {
          vertical: "top",
          horizontal: "right",
        },
      });
    }
  }, [connectionStatus, core.relayer, setConnectionStatus, t]);

  useEffect(() => {
    const onConnect = () => {
      setConnectionStatus("connected");
      enqueueSnackbar(t("connect.connected"), {
        connected: true,
        variant: "connectionNotification",
        anchorOrigin: {
          vertical: "top",
          horizontal: "right",
        },
      });
    };

    const onDisconnect = () => {
      setConnectionStatus("disconnected");
      enqueueSnackbar(t("connect.disconnected"), {
        connected: false,
        variant: "connectionNotification",
        anchorOrigin: {
          vertical: "top",
          horizontal: "right",
        },
      });
    };

    core.relayer.on("relayer_connect", onConnect);
    core.relayer.on("relayer_disconnect", onDisconnect);

    return () => {
      core.relayer.off("relayer_connect", onConnect);
      core.relayer.off("relayer_disconnect", onDisconnect);
    };
  }, [core.relayer, setConnectionStatus, t]);
}

export default function useWalletConnect() {
  const { t } = useTranslation();
  useWalletConnectStatus();

  const navigate = useNavigate({ from: "/" });
  const walletKit = useAtomValue(walletKitAtom);

  const queryClient = useQueryClient();

  const client = useAtomValue(walletAPIClientAtom);
  const walletInfo = useAtomValue(walletInfoAtom);
  const walletCapabilities = useAtomValue(walletCapabilitiesAtom);
  const addVerifyContext = useSetAtom(addVerifyContextAtom);
  const getPendingKeys = useCallback(
    () => [
      ...walletKit.engine.signClient.proposal.keys.map((id) =>
        getVerifyContextKey("proposal", id),
      ),
      ...walletKit.engine.signClient.auth.requests.keys.map((id) =>
        getVerifyContextKey("auth", id),
      ),
    ],
    [walletKit],
  );

  const accounts = useAccounts(client);

  const onProposalExpire = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: pendingProposalsQueryKey,
    });
  }, [queryClient]);

  const pendingProposalsQueryFn = usePendingProposalsQueryFn(walletKit);

  const onSessionProposal = useCallback(
    (proposal: WalletKitTypes.SessionProposal) => {
      addVerifyContext({
        key: getVerifyContextKey("proposal", proposal.id),
        context: proposal.verifyContext,
        pendingKeys: getPendingKeys(),
      });

      void queryClient
        .invalidateQueries({
          queryKey: pendingProposalsQueryKey,
        })
        .then(() => {
          // Prefetching as we need the data in the next route to avoid redirecting to home
          return queryClient.prefetchQuery({
            queryKey: pendingProposalsQueryKey,
            queryFn: pendingProposalsQueryFn,
          });
        })
        .then(() => {
          return navigate({
            to: "/proposal/$id",
            params: { id: proposal.id.toString() },
            search: (search) => search,
          });
        });
    },
    [
      navigate,
      pendingProposalsQueryFn,
      queryClient,
      addVerifyContext,
      getPendingKeys,
    ],
  );

  const setShowModal = useSetAtom(showBackToBrowserModalAtom);
  const redirectToDapp = useCallback(
    (topic: string) => {
      const session = walletKit.engine.signClient.session.get(topic);
      if (session) {
        const url = getSafeRedirectUrl(session.peer.metadata.redirect);
        if (url) {
          window.open(url);
        } else {
          setShowModal(true);
        }
      }
    },
    [setShowModal, walletKit.engine.signClient.session],
  );

  const setLoading = useSetAtom(loadingAtom);
  const onSessionRequest = useCallback(
    (requestEvent: SignClientTypes.EventArguments["session_request"]) => {
      const {
        topic,
        params: { request, chainId },
        id,
      } = requestEvent;

      void (async () => {
        try {
          if (isWalletRequest(request)) {
            await handleWalletRequest(
              request,
              topic,
              id,
              chainId,
              accounts.data,
              client,
              walletKit,
              queryClient,
              walletInfo,
              walletCapabilities,
            );
          } else if (isEIP155Chain(chainId, request)) {
            await handleEIP155Request(
              request,
              topic,
              id,
              chainId,
              accounts.data,
              client,
              walletKit,
            );
          } else if (isBIP122Chain(chainId, request)) {
            const session =
              walletKit.engine.signClient.session.get(topic);
            const bip122Accounts =
              session?.namespaces.bip122?.accounts ?? [];
            const sessionAddress = bip122Accounts
              .find((a) => a.startsWith(chainId + ":"))
              ?.split(":")[2];

            await handleBIP122Request(
              request,
              topic,
              id,
              chainId,
              {
                accounts: accounts.data,
                client,
                walletkit: walletKit,
                walletCapabilities,
                sessionAddress,
              },
            );
          } else if (
            isRippleChain(chainId, request) &&
            isXRPLSupportEnabled(walletCapabilities)
          ) {
            await handleXrpRequest(
              request,
              topic,
              id,
              chainId,
              accounts.data,
              client,
              walletKit,
            );
          } else if (
            isSolanaChain(chainId, request) &&
            isSolanaSupportEnabled(walletInfo)
          ) {
            await handleSolanaRequest(
              request,
              topic,
              id,
              chainId,
              accounts.data,
              client,
              walletKit,
            );
          } else if (
            isTezosChain(chainId, request) &&
            isTezosSupportEnabled(walletCapabilities)
          ) {
            const session = walletKit.engine.signClient.session.get(topic);
            const sessionTezosAddresses = new Set(
              (session?.namespaces.tezos?.accounts ?? []).map(
                (account) => account.split(":")[2],
              ),
            );
            await handleTezosRequest(
              request,
              topic,
              id,
              chainId,
              accounts.data.filter((account) =>
                sessionTezosAddresses.has(account.address),
              ),
              client,
              walletKit,
            );
          } else if (
            isCosmosChain(chainId, request) &&
            isCosmosSupportEnabled(walletCapabilities)
          ) {
            const session = walletKit.engine.signClient.session.get(topic);
            const sessionCosmosAddresses = new Set(
              (session?.namespaces.cosmos?.accounts ?? []).map(
                (account) => account.split(":")[2],
              ),
            );
            await handleCosmosRequest(
              request,
              topic,
              id,
              chainId,
              accounts.data.filter((account) =>
                sessionCosmosAddresses.has(account.address),
              ),
              client,
              walletKit,
            );
          } else {
            console.error("Not Supported Chain");
            await rejectRequest(
              walletKit,
              topic,
              id,
              Errors.unsupportedChains,
              5100,
            );
          }
        } catch (error) {
          if (error instanceof ZodError) {
            reportZodError({
              error,
              topic,
              request: { method: request.method },
              chainId,
              walletKit,
            });
          }

          const message =
            error instanceof ZodError
              ? t("error.zodRequestBlocked")
              : getErrorMessage(error);

          enqueueSnackbar(message, {
            errorType: "Session request error",
            variant: "errorNotification",
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
          });

          await rejectRequest(walletKit, topic, id, Errors.txDeclined);
        }
      })().finally(() => {
        setLoading(false);
        redirectToDapp(topic);
        void queryClient.invalidateQueries({ queryKey: sessionsQueryKey });
      });
    },
    [
      walletCapabilities,
      walletInfo,
      accounts.data,
      client,
      walletKit,
      queryClient,
      t,
      setLoading,
      redirectToDapp,
    ],
  );

  const onSessionDeleted = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: sessionsQueryKey });
  }, [queryClient]);

  const setOneClickAuthPayload = useSetAtom(oneClickAuthPayloadAtom);
  const onSessionAuthenticate = useCallback(
    (payload: OneClickAuthPayload) => {
      addVerifyContext({
        key: getVerifyContextKey("auth", payload.id),
        context: payload.verifyContext,
        pendingKeys: getPendingKeys(),
      });

      setOneClickAuthPayload(payload);
      void navigate({
        to: "/oneclickauth",
        search: (search) => search,
      });
    },
    [navigate, setOneClickAuthPayload, addVerifyContext, getPendingKeys],
  );

  useEffect(() => {
    console.log("walletKit setup listeners");
    // TODO: handle session_request_expire
    // walletKit.on("session_request_expire", );

    // sign
    walletKit.on("proposal_expire", onProposalExpire);
    walletKit.on("session_proposal", onSessionProposal);
    walletKit.on("session_request", onSessionRequest);
    walletKit.on("session_delete", onSessionDeleted);
    walletKit.on("session_authenticate", onSessionAuthenticate);

    // auth
    // walletKit.on("auth_request", onAuthRequest);
    return () => {
      console.log("walletKit cleanup listeners");
      // sign
      walletKit.off("proposal_expire", onProposalExpire);
      walletKit.off("session_proposal", onSessionProposal);
      walletKit.off("session_request", onSessionRequest);
      walletKit.off("session_delete", onSessionDeleted);
      walletKit.off("session_authenticate", onSessionAuthenticate);

      // auth
      // walletKit.off("auth_request", onAuthRequest);
    };
  }, [
    walletKit,
    onSessionProposal,
    onSessionRequest,
    onSessionDeleted,
    onProposalExpire,
    onSessionAuthenticate,
  ]);
}
